import { useEffect, useState } from "react";
import {
  SRCSET_VERIFY_EVENT,
  clearVerifications,
  loadVerifications,
  type SrcSetVerification,
} from "@/lib/srcset-verify";

/**
 * History of srcset candidate verifications recorded by the QA hero frame
 * (including framed runs launched from this dashboard).
 */
export const SrcsetVerifyPanel = () => {
  const [rows, setRows] = useState<SrcSetVerification[]>([]);

  useEffect(() => {
    setRows(loadVerifications());
    const refresh = () => setRows(loadVerifications());
    window.addEventListener(SRCSET_VERIFY_EVENT, refresh);
    window.addEventListener("storage", refresh);
    const timer = window.setInterval(refresh, 2000);
    return () => {
      window.removeEventListener(SRCSET_VERIFY_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.clearInterval(timer);
    };
  }, []);

  const failures = rows.filter((r) => !r.pass).length;

  return (
    <section className="rounded border border-border/60 bg-navy-elevated/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-sm uppercase tracking-[0.2em] text-gold">
            Srcset candidate verifier
          </h2>
          <p className="mt-1 text-xs text-silver/70">
            Records the exact image URL the browser selected per viewport and
            whether the decode stayed inside the breakpoint target.{" "}
            {rows.length} check{rows.length === 1 ? "" : "s"} ·{" "}
            <span className={failures ? "text-rose-300" : "text-emerald"}>
              {failures} failing
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            clearVerifications();
            setRows([]);
          }}
          disabled={rows.length === 0}
          className="rounded border border-border px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
        >
          Clear checks
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-silver/60">
          No verifications yet — run a measurement above, or open{" "}
          <span className="font-mono">/qa/hero/frame?perf=1</span>.
        </p>
      ) : (
        <div className="mt-3 max-h-80 overflow-auto">
          <table className="w-full min-w-[820px] text-xs">
            <thead className="sticky top-0 bg-navy-elevated text-silver/60">
              <tr>
                {[
                  "When",
                  "Breakpoint",
                  "Viewport",
                  "DPR",
                  "Selected file",
                  "Chosen / needed",
                  "Verdict",
                  "Decode / target",
                  "Result",
                ].map((h) => (
                  <th key={h} className="p-2 text-left font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono">
              {rows.slice(0, 60).map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="p-2 text-silver/60">
                    {new Date(r.at).toLocaleTimeString()}
                  </td>
                  <td className="p-2 capitalize">{r.breakpoint}</td>
                  <td className="p-2 tabular-nums">{r.viewportWidth}</td>
                  <td className="p-2 tabular-nums">{r.dpr}</td>
                  <td className="p-2 max-w-[220px] truncate" title={r.chosenUrl}>
                    {r.chosenFile}
                  </td>
                  <td className="p-2 tabular-nums">
                    {r.chosenWidth ?? "?"}w / {r.requiredWidth ?? "?"}px
                  </td>
                  <td
                    className={`p-2 ${
                      r.verdict === "optimal"
                        ? "text-emerald"
                        : r.verdict === "unknown"
                          ? "text-amber-300"
                          : "text-rose-300"
                    }`}
                  >
                    {r.verdict}
                    {r.overshootPct != null ? ` (${r.overshootPct}%)` : ""}
                  </td>
                  <td
                    className={`p-2 tabular-nums ${
                      r.decodeWithinTarget ? "text-emerald" : "text-rose-300"
                    }`}
                  >
                    {r.decodeMs ?? "—"} / {r.decodeTargetMs} ms
                  </td>
                  <td className="p-2">
                    <span
                      className={
                        r.pass
                          ? "rounded bg-emerald/20 px-1.5 py-0.5 text-emerald"
                          : "rounded bg-rose-500/20 px-1.5 py-0.5 text-rose-300"
                      }
                    >
                      {r.pass ? "PASS" : "FAIL"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default SrcsetVerifyPanel;
